import { useState, useEffect } from 'react';
import { updateProfile, changePassword, listApiKeys, createApiKey, revokeApiKey } from '../../services/auth';
import { useTheme, THEMES, FONT_SCALES, FONTS } from '../../contexts/ThemeContext';
import GroupManager from './GroupManager';
import FollowManager from './FollowManager';
import CarManager from './CarManager';
import DesktopSyncSettings from './DesktopSyncSettings';
import styles from './AccountSettings.module.css';
import unitStyles from './PreferencesTab.module.css';

const STORAGE_KEY = 'trackapp:driver_profile';
const profileStorageKey = userId => userId ? `${STORAGE_KEY}:${userId}` : STORAGE_KEY;

const EXPERIENCE_LEVELS = [
  { value: 'hpde_novice',       label: 'HPDE Novice' },
  { value: 'hpde_intermediate', label: 'HPDE Intermediate' },
  { value: 'hpde_advanced',     label: 'HPDE Advanced' },
  { value: 'scca_solo',         label: 'SCCA / Club Racing' },
  { value: 'instructor',        label: 'Instructor' },
  { value: 'pro',               label: 'Professional' },
];

export function loadDriverProfile(userId = null, migrateLegacy = false) {
  try {
    const key = profileStorageKey(userId);
    let raw = localStorage.getItem(key);
    if (!raw && userId && migrateLegacy) {
      raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        localStorage.setItem(key, raw);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function AccountSettings({
  onClose, onSave, currentUser, initialTab = 'profile',
  capabilities = null,
  unitPrefs, onUnitPrefsChange, autoShare, onAutoShareChange,
  initialCarId, autoOpenPhysics, liveSessionCarId, liveValue, onLiveChange,
  sessionId, racer, onMetaSaved,
}) {
  const userAccounts = capabilities?.user_accounts !== false;
  const desktopSync = capabilities?.sync_to_server === true;
  const serverOnlyTabs = new Set(['groups', 'following', 'security', 'apikeys']);
  const { theme, setTheme, fontScale, setFontScale, fontFamily, setFontFamily } = useTheme();
  const [activeTab, setActiveTab] = useState(
    userAccounts || !serverOnlyTabs.has(initialTab) ? initialTab : 'profile'
  );
  const [form, setForm] = useState(() => ({
    name:       currentUser?.display_name    || loadDriverProfile(currentUser?.id, !currentUser?.masquerade)?.name       || '',
    experience: currentUser?.driver_experience || loadDriverProfile(currentUser?.id, !currentUser?.masquerade)?.experience || 'hpde_intermediate',
    car:        currentUser?.driver_car       || loadDriverProfile(currentUser?.id, !currentUser?.masquerade)?.car        || '',
    notes:      currentUser?.driver_notes     || loadDriverProfile(currentUser?.id, !currentUser?.masquerade)?.notes      || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError]   = useState(null);
  const [pwSuccess, setPwSuccess] = useState(false);

  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeyLabel, setApiKeyLabel] = useState('');
  const [apiKeyCreating, setApiKeyCreating] = useState(false);
  const [apiKeyError, setApiKeyError] = useState(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);

  useEffect(() => {
    if (userAccounts && activeTab === 'apikeys') {
      listApiKeys().then(setApiKeys).catch(() => {});
    }
  }, [activeTab, userAccounts]);

  const handleCreateApiKey = async () => {
    if (!apiKeyLabel.trim()) return;
    setApiKeyCreating(true);
    setApiKeyError(null);
    try {
      const created = await createApiKey(apiKeyLabel.trim());
      setNewlyCreatedKey(created.raw_key);
      setApiKeyLabel('');
      const updated = await listApiKeys();
      setApiKeys(updated);
    } catch (e) {
      setApiKeyError(e.message || 'Failed to create key');
    } finally {
      setApiKeyCreating(false);
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    try {
      await revokeApiKey(keyId);
      setApiKeys(prev => prev.map(k => k.id === keyId ? { ...k, is_revoked: true } : k));
    } catch {
      // silently ignore — the row will show stale state at worst
    }
  };

  const setPw = (field, val) => { setPwForm(prev => ({ ...prev, [field]: val })); setPwSuccess(false); };

  const handleChangePassword = async () => {
    setPwError(null);
    if (pwForm.next.length < 8) { setPwError('New password must be at least 8 characters'); return; }
    if (pwForm.next !== pwForm.confirm) { setPwError('Passwords do not match'); return; }
    setPwSaving(true);
    try {
      await changePassword(pwForm.current, pwForm.next);
      setPwForm({ current: '', next: '', confirm: '' });
      setPwSuccess(true);
    } catch (e) {
      setPwError(e.message || 'Password change failed');
    } finally {
      setPwSaving(false);
    }
  };

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateProfile({
        display_name:       form.name,
        driver_experience:  form.experience,
        driver_car:         form.car,
        driver_notes:       form.notes,
      });
      localStorage.setItem(profileStorageKey(currentUser?.id), JSON.stringify(form));
      onSave?.(form, updated);
      onClose?.();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Close on Escape
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose?.()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span className={styles.title}>Settings</span>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'profile' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'preferences' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('preferences')}
          >
            Preferences
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'cars' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('cars')}
          >
            Cars
          </button>
          {desktopSync && (
            <button
              className={`${styles.tab} ${activeTab === 'sync' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('sync')}
            >
              Server Sync
            </button>
          )}
          {userAccounts && <>
            <button
              className={`${styles.tab} ${activeTab === 'groups' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('groups')}
            >
              Groups
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'following' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('following')}
            >
              Following
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'security' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('security')}
            >
              Security
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'apikeys' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('apikeys')}
            >
              API Keys
            </button>
          </>}
        </div>

        <div className={styles.body}>
          {activeTab === 'cars' && (
            <CarManager
              initialCarId={initialCarId}
              autoOpenPhysics={autoOpenPhysics}
              liveSessionCarId={liveSessionCarId}
              liveValue={liveValue}
              onLiveChange={onLiveChange}
              sessionId={sessionId}
              racer={racer}
              onMetaSaved={onMetaSaved}
            />
          )}
          {desktopSync && activeTab === 'sync' && <DesktopSyncSettings sessionId={sessionId} />}
          {userAccounts && activeTab === 'groups' && <GroupManager />}
          {userAccounts && activeTab === 'following' && <FollowManager />}
          {activeTab === 'preferences' && unitPrefs && (() => {
            const setPref = (key, val) => onUnitPrefsChange({ ...unitPrefs, [key]: val });
            return (
              <>
                <div className={unitStyles.field}>
                  <span className={unitStyles.fieldLabel}>Temperature</span>
                  <div className={unitStyles.segmented}>
                    <button
                      className={`${unitStyles.seg} ${unitPrefs.temp === 'C' ? unitStyles.segActive : ''}`}
                      onClick={() => setPref('temp', 'C')}
                    >°C — Celsius</button>
                    <button
                      className={`${unitStyles.seg} ${unitPrefs.temp === 'F' ? unitStyles.segActive : ''}`}
                      onClick={() => setPref('temp', 'F')}
                    >°F — Fahrenheit</button>
                  </div>
                </div>

                <div className={unitStyles.field}>
                  <span className={unitStyles.fieldLabel}>Speed</span>
                  <div className={unitStyles.segmented}>
                    <button
                      className={`${unitStyles.seg} ${unitPrefs.speed === 'mph' ? unitStyles.segActive : ''}`}
                      onClick={() => setPref('speed', 'mph')}
                    >mph</button>
                    <button
                      className={`${unitStyles.seg} ${unitPrefs.speed === 'km/h' ? unitStyles.segActive : ''}`}
                      onClick={() => setPref('speed', 'km/h')}
                    >km/h</button>
                  </div>
                </div>

                <div className={unitStyles.field}>
                  <span className={unitStyles.fieldLabel}>Pressure</span>
                  <div className={unitStyles.segmented}>
                    <button
                      className={`${unitStyles.seg} ${unitPrefs.pressure === 'psi' ? unitStyles.segActive : ''}`}
                      onClick={() => setPref('pressure', 'psi')}
                    >psi</button>
                    <button
                      className={`${unitStyles.seg} ${unitPrefs.pressure === 'bar' ? unitStyles.segActive : ''}`}
                      onClick={() => setPref('pressure', 'bar')}
                    >bar</button>
                    <button
                      className={`${unitStyles.seg} ${unitPrefs.pressure === 'kPa' ? unitStyles.segActive : ''}`}
                      onClick={() => setPref('pressure', 'kPa')}
                    >kPa</button>
                  </div>
                </div>

                <div className={unitStyles.field}>
                  <label className={unitStyles.checkRow}>
                    <input
                      type="checkbox"
                      className={unitStyles.checkInput}
                      checked={!!unitPrefs.anchoredTooltip}
                      onChange={e => setPref('anchoredTooltip', e.target.checked)}
                    />
                    <span className={unitStyles.fieldLabel}>Pin tooltip to chart edge</span>
                  </label>
                  <span className={unitStyles.checkHint}>Prevents trace overlap on small screens</span>
                </div>

                <div className={unitStyles.field}>
                  <span className={unitStyles.fieldLabel}>Hover tooltip text size</span>
                  <div className={unitStyles.segmented}>
                    {['small', 'medium', 'large'].map(v => (
                      <button key={v}
                        className={`${unitStyles.seg} ${(unitPrefs.tooltipFontScale ?? 'medium') === v ? unitStyles.segActive : ''}`}
                        onClick={() => setPref('tooltipFontScale', v)}
                      >{v[0].toUpperCase() + v.slice(1)}</button>
                    ))}
                  </div>
                </div>

                {onAutoShareChange && (
                  <div className={unitStyles.field}>
                    <label className={unitStyles.checkRow}>
                      <input
                        type="checkbox"
                        className={unitStyles.checkInput}
                        checked={!!autoShare}
                        onChange={e => onAutoShareChange(e.target.checked)}
                      />
                      <span className={unitStyles.fieldLabel}>Auto-share new uploads with everyone</span>
                    </label>
                    <span className={unitStyles.checkHint}>
                      New sessions become visible to all logged-in users, who can view and comment.
                      Existing sessions are unaffected.
                    </span>
                  </div>
                )}
              </>
            );
          })()}
          {activeTab === 'profile' && <>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>UI Theme</span>
            <div className={styles.themeRow}>
              {Object.entries(THEMES).map(([key, def]) => (
                <button
                  key={key}
                  className={`${styles.themeChip} ${theme === key ? styles.themeChipActive : ''}`}
                  style={{ '--chip-accent': def['--accent'] }}
                  onClick={() => setTheme(key)}
                  type="button"
                >
                  <span className={styles.themeChipDot} />
                  {def.label}
                </button>
              ))}
            </div>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Text Size</span>
            <div className={styles.themeRow}>
              {Object.entries(FONT_SCALES).map(([key, def]) => (
                <button
                  key={key}
                  className={`${styles.themeChip} ${fontScale === key ? styles.themeChipActive : ''}`}
                  style={{ '--chip-accent': 'var(--accent)', fontSize: `${def.scale * 0.78}rem` }}
                  onClick={() => setFontScale(key)}
                  type="button"
                >
                  {def.label}
                </button>
              ))}
            </div>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Font</span>
            <div className={styles.themeRow}>
              {Object.entries(FONTS).map(([key, def]) => (
                <button
                  key={key}
                  className={`${styles.themeChip} ${fontFamily === key ? styles.themeChipActive : ''}`}
                  style={{ '--chip-accent': 'var(--accent)', fontFamily: def.stack }}
                  onClick={() => setFontFamily(key)}
                  type="button"
                >
                  {def.label}
                </button>
              ))}
            </div>
          </label>

          {userAccounts && currentUser?.email && (
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Account Email</span>
              <input
                className={styles.input}
                type="text"
                value={currentUser.email}
                readOnly
                style={{ opacity: 0.6, cursor: 'default' }}
              />
            </label>
          )}

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Name</span>
            <input
              className={styles.input}
              type="text"
              placeholder="Your name"
              value={form.name}
              onChange={e => set('name', e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Experience Level</span>
            <select
              className={styles.select}
              value={form.experience}
              onChange={e => set('experience', e.target.value)}
            >
              {EXPERIENCE_LEVELS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Car</span>
            <input
              className={styles.input}
              type="text"
              placeholder="e.g. E36 BMW LS3 — Hoosier A7"
              value={form.car}
              onChange={e => set('car', e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Notes for coach</span>
            <textarea
              className={styles.textarea}
              rows={3}
              placeholder="Anything the LLM should know: goals, problem areas, recent changes…"
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
            />
          </label>
          </>}
          {userAccounts && activeTab === 'apikeys' && <>
            {newlyCreatedKey && (
              <div className={styles.keyReveal}>
                <span className={styles.fieldLabel}>New API Key — copy it now, it won't be shown again</span>
                <div className={styles.keyBox}>
                  <code className={styles.keyCode}>{newlyCreatedKey}</code>
                  <button
                    className={styles.copyBtn}
                    onClick={() => { navigator.clipboard.writeText(newlyCreatedKey); }}
                  >Copy</button>
                </div>
                <button className={styles.dismissBtn} onClick={() => setNewlyCreatedKey(null)}>Dismiss</button>
              </div>
            )}
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Create New Key</span>
              <div className={styles.keyCreate}>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="Label (e.g. My Upload Script)"
                  value={apiKeyLabel}
                  onChange={e => setApiKeyLabel(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreateApiKey()}
                />
                <button
                  className={styles.saveBtn}
                  onClick={handleCreateApiKey}
                  disabled={apiKeyCreating || !apiKeyLabel.trim()}
                >
                  {apiKeyCreating ? 'Creating…' : 'Create'}
                </button>
              </div>
              {apiKeyError && <span className={styles.errorMsg}>{apiKeyError}</span>}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Your Keys</span>
              {apiKeys.length === 0 && <span className={styles.noKeys}>No API keys yet.</span>}
              <ul className={styles.keyList}>
                {apiKeys.map(k => (
                  <li key={k.id} className={`${styles.keyItem} ${k.is_revoked ? styles.keyRevoked : ''}`}>
                    <div className={styles.keyMeta}>
                      <span className={styles.keyLabel}>{k.label}</span>
                      <span className={styles.keyDate}>
                        Created {new Date(k.created_at).toLocaleDateString()}
                        {k.last_used_at && ` · Last used ${new Date(k.last_used_at).toLocaleDateString()}`}
                        {k.is_revoked && ' · Revoked'}
                      </span>
                    </div>
                    {!k.is_revoked && (
                      <button
                        className={styles.revokeBtn}
                        onClick={() => handleRevokeApiKey(k.id)}
                      >Revoke</button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </>}
          {userAccounts && activeTab === 'security' && <>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Current Password</span>
              <input
                className={styles.input}
                type="password"
                placeholder="Enter current password"
                value={pwForm.current}
                onChange={e => setPw('current', e.target.value)}
                autoComplete="current-password"
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>New Password</span>
              <input
                className={styles.input}
                type="password"
                placeholder="At least 8 characters"
                value={pwForm.next}
                onChange={e => setPw('next', e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Confirm New Password</span>
              <input
                className={styles.input}
                type="password"
                placeholder="Repeat new password"
                value={pwForm.confirm}
                onChange={e => setPw('confirm', e.target.value)}
                autoComplete="new-password"
              />
            </label>
          </>}
        </div>

        {activeTab === 'profile' && (
          <div className={styles.footer}>
            {error && <span className={styles.errorMsg}>{error}</span>}
            <button className={styles.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
            <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
          </div>
        )}
        {activeTab === 'preferences' && (
          <div className={styles.footer}>
            <button className={styles.cancelBtn} onClick={onClose}>Close</button>
          </div>
        )}
        {activeTab === 'groups' && (
          <div className={styles.footer}>
            <button className={styles.cancelBtn} onClick={onClose}>Close</button>
          </div>
        )}
        {activeTab === 'following' && (
          <div className={styles.footer}>
            <button className={styles.cancelBtn} onClick={onClose}>Close</button>
          </div>
        )}
        {activeTab === 'apikeys' && (
          <div className={styles.footer}>
            <button className={styles.cancelBtn} onClick={onClose}>Close</button>
          </div>
        )}
        {activeTab === 'security' && (
          <div className={styles.footer}>
            {pwError && <span className={styles.errorMsg}>{pwError}</span>}
            {pwSuccess && <span className={styles.successMsg}>Password updated!</span>}
            <button className={styles.cancelBtn} onClick={onClose} disabled={pwSaving}>Cancel</button>
            <button className={styles.saveBtn} onClick={handleChangePassword} disabled={pwSaving}>
              {pwSaving ? 'Updating…' : 'Change Password'}
            </button>
          </div>
        )}
        {activeTab === 'sync' && (
          <div className={styles.footer}>
            <button className={styles.cancelBtn} onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
